import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import {
  Sparkle, Robot, CheckCircle, Warning, BookmarkSimple,
  Clock, Lightning, ArrowRight,
} from "@phosphor-icons/react";
import { useErp, AGENTS } from "@/context/ErpContext";

const AGENT_LABEL = AGENTS.reduce((m, a) => { m[a.key] = a.label; return m; }, {});
const AGENT_CATEGORY = AGENTS.reduce((m, a) => { m[a.key] = a.category; return m; }, {});

// Distinct colour palette cycled per agent bar in the "Top Agents by Runs" chart.
const AGENT_BAR_COLORS = [
  "#0020C2", // blue
];

const OracleProfileBanner = () => {
  const { profile, setSetupOpen } = useErp();
  if (!profile || profile.configured) return null;
  return (
    <div data-testid="oracle-profile-banner"
      className="flex items-start justify-between gap-4 border border-[#EAB308] bg-yellow-50 px-4 py-3">
      <div className="flex items-start gap-2.5">
        <Sparkle size={18} weight="fill" className="text-[#EAB308] mt-0.5 shrink-0" />
        <div>
          <div className="text-sm font-bold text-gray-900">Connect your Oracle Fusion profile</div>
          <div className="text-xs text-gray-600 mt-0.5">Set up your Oracle credentials, Pod URL and defaults (BU, Ledger, Legal Employer) to run ERP AI Agents under your own identity.</div>
        </div>
      </div>
      <button onClick={() => setSetupOpen(true)} data-testid="oracle-profile-banner-cta"
        className="shrink-0 bg-[#0020C2] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-[#001AA3]">
        Set up now
      </button>
    </div>
  );
};

const KPI = ({ label, value, sub, testid, icon: Icon, accent, accentBg }) => (
  <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
    <div className={`absolute inset-x-0 top-0 h-1 ${accentBg || "bg-gradient-to-r from-[#0020C2] to-[#3B5BFF]"} opacity-80`} />
    <div className="flex items-center justify-between">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">{label}</div>
      {Icon && (
        <div className={`p-2 rounded-lg ${accentBg ? "bg-white" : "bg-gray-50"} ${accent || "text-gray-400"} group-hover:scale-110 transition-transform`}>
          <Icon size={16} weight="bold" />
        </div>
      )}
    </div>
    <div className="mt-3 text-4xl font-black font-mono tracking-tight text-gray-900" data-testid={testid}>
      {value}
    </div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

const EmptyState = ({ icon: Icon = Sparkle, title, body, action }) => (
  <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
    <div className="mb-3 p-3 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 ring-1 ring-blue-100">
      <Icon size={24} weight="duotone" className="text-[#0020C2]" />
    </div>
    <div className="text-sm font-semibold text-gray-900">{title}</div>
    {body && <div className="text-xs text-gray-500 mt-1 max-w-xs">{body}</div>}
    {action}
  </div>
);

const StatusPill = ({ status }) => {
  const ok = status >= 200 && status < 400;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold tracking-wider uppercase border ${
      ok ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-red-300 bg-red-50 text-[#DC2626]"
    }`}>
      {ok ? <CheckCircle size={10} weight="fill" /> : <Warning size={10} weight="fill" />}
      {status || "—"}
    </span>
  );
};

const timeUntil = (iso) => {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "due now";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `in ${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `in ${d}d`;
};

const cadenceText = (s) => {
  if (s.cadence === "daily") return `Daily · ${String(s.hour_utc).padStart(2, "0")}:00 UTC`;
  if (s.cadence === "weekly") {
    const dow = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][s.day_of_week] || "Mon";
    return `Weekly · ${dow} · ${String(s.hour_utc).padStart(2, "0")}:00 UTC`;
  }
  return `Monthly · day ${s.day_of_month} · ${String(s.hour_utc).padStart(2, "0")}:00 UTC`;
};

export default function DashboardPage() {
  const [records, setRecords] = useState([]);
  const [savedQueries, setSavedQueries] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scroll smoothly to the top of the page whenever the dashboard loads/mounts,
  // so arriving here from a scrolled-down page never leaves you mid-page.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

 const refresh = async () => {
  try {
    const me = await api.get("/auth/me");

    const agents = me.data.agents || [];

    const formattedRecords = agents.map((agent, index) => ({
      id: index + 1,
      agent_key: agent,
      agent_name: agent,
      oracle_status: 200,
      prompt: "Assigned Agent",
      created_at: new Date().toISOString(),
      created_by: me.data.email,
    }));

    setRecords(formattedRecords);
    setSavedQueries([]);
    setSchedules([]);
  } catch (err) {
    console.error("Dashboard Load Error:", err);
    setRecords([]);
    setSavedQueries([]);
    setSchedules([]);
  } finally {
    setLoading(false);
  }
};
  useEffect(() => { refresh(); }, []);

  const stats = useMemo(() => {
    const total = records.length;
    const success = records.filter((r) => r.oracle_status >= 200 && r.oracle_status < 400).length;
    const failed = total - success;
    return { total, success, failed };
  }, [records]);

  const nowMs = Date.now();
  const activeSchedules = schedules.filter((s) => s.enabled).length;
  const dueNow = schedules.filter((s) => s.enabled && new Date(s.next_run_at).getTime() <= nowMs).length;

  const runsByAgent = useMemo(() => {
    const m = {};
    for (const r of records) {
      const k = r.agent_key;
      if (!m[k]) m[k] = { agent_key: k, label: AGENT_LABEL[k] || r.agent_name || k, runs: 0, fail: 0 };
      m[k].runs += 1;
      if (!(r.oracle_status >= 200 && r.oracle_status < 400)) m[k].fail += 1;
    }
    return Object.values(m).sort((a, b) => b.runs - a.runs).slice(0, 8);
  }, [records]);

  const recentRuns = records.slice(0, 8);
  const upcoming = schedules
    .filter((s) => s.enabled)
    .slice() // already sorted by next_run_at on backend
    .slice(0, 5);

  // Latest finance-anomaly insight to spotlight
  const latestInsight = useMemo(() => {
    return records.find(
      (r) => (r.agent_key === "finance-anomaly" || r.agent_key === "finance-variance" || r.agent_key === "finance-narrative")
        && r.insights && r.insights.headline
    );
  }, [records]);

  return (
    <div>
      <PageHeader
        eyebrow="ERP AI Operations"
        title="Agent Command Center"
        subtitle="Live view of every Oracle Fusion agent run, saved query, and automation across your tenant."
        testid="dashboard-title"
        actions={
          <Link to="/automations" data-testid="dashboard-automations-link"
            className="bg-[#0020C2] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-[#001AA3] inline-flex items-center gap-1.5">
            Manage Automations <ArrowRight size={12} weight="bold" />
          </Link>
        }
      />

      <div className="p-8 space-y-6">
        <OracleProfileBanner />

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <KPI testid="kpi-total-runs" label="Total Agent Runs" value={stats.total} sub="All-time across tenant" icon={Robot}
            accent="text-[#0020C2]" accentBg="bg-gradient-to-r from-[#0020C2] to-[#3B5BFF]" />
          <KPI testid="kpi-success-runs" label="Successful" value={stats.success} sub={`${stats.total ? Math.round((stats.success / stats.total) * 100) : 0}% success rate`} icon={CheckCircle}
            accent="text-emerald-600" accentBg="bg-gradient-to-r from-emerald-500 to-emerald-400" />
          <KPI testid="kpi-failed-runs" label="Failed" value={stats.failed} sub="HTTP ≥ 400 from Oracle" icon={Warning}
            accent="text-[#DC2626]" accentBg="bg-gradient-to-r from-[#DC2626] to-rose-400" />
          <KPI testid="kpi-saved-queries" label="Saved Queries" value={savedQueries.length} sub="Ready to replay" icon={BookmarkSimple}
            accent="text-amber-600" accentBg="bg-gradient-to-r from-amber-500 to-amber-300" />
          <KPI testid="kpi-active-schedules" label="Active Schedules" value={activeSchedules} sub={dueNow > 0 ? `${dueNow} due now` : "Auto-runs configured"} icon={Clock}
            accent={dueNow > 0 ? "text-orange-500" : "text-violet-600"} accentBg="bg-gradient-to-r from-violet-500 to-fuchsia-400" />
        </div>

        {/* Latest AI Insight spotlight (only when present) */}
        {latestInsight && (
          <div data-testid="dashboard-latest-insight"
            className="border-l-4 border-[#0020C2] border-t border-r border-b border-gray-200 bg-white p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
                  <Lightning size={12} weight="fill" /> Latest AI Insight · {AGENT_LABEL[latestInsight.agent_key] || latestInsight.agent_name}
                </div>
                <h3 className="text-xl font-black tracking-tight mt-1 text-gray-900">{latestInsight.insights.headline}</h3>
                {Array.isArray(latestInsight.insights.bullets) && latestInsight.insights.bullets.length > 0 && (
                  <ul className="mt-3 text-sm text-gray-700 space-y-1 list-disc pl-5">
                    {latestInsight.insights.bullets.slice(0, 3).map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                )}
              </div>
              <div className="shrink-0 text-[11px] font-mono text-gray-500">{fmtDate(latestInsight.created_at)}</div>
            </div>
          </div>
        )}

        {/* Runs by agent + Upcoming automations */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 ">
          <div className="lg:col-span-3 rounded-xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="dashboard-runs-by-agent">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Agent Usage</div>
                <h3 className="text-xl font-black tracking-tight mt-1">Top Agents by Runs</h3>
              </div>
              <div className="text-xs font-mono text-gray-500">Last {records.length} records</div>
            </div>
            {runsByAgent.length === 0 ? (
              <EmptyState
                icon={Robot}
                title={loading ? "Loading agent activity…" : "No agent runs yet"}
                body={loading ? null : "Pick an agent from the sidebar to launch your first Oracle Fusion run."}
              />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={runsByAgent} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                  <CartesianGrid stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="label" stroke="#9CA3AF" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                    interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis stroke="#9CA3AF" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }} />
                  <Bar dataKey="runs" fill="#0020C2" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="fail" fill="#DC2626" radius={[0, 0, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="lg:col-span-2 rounded-xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="dashboard-upcoming-automations">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Automations</div>
                <h3 className="text-xl font-black tracking-tight mt-1">Upcoming Runs</h3>
              </div>
              <Link to="/automations" className="text-[11px] font-bold uppercase tracking-wider text-[#0020C2] hover:underline">
                View all
              </Link>
            </div>
            {upcoming.length === 0 ? (
              <EmptyState
                icon={Clock}
                title="No active schedules yet"
                body="Save a query from any agent then schedule it to run daily, weekly, or monthly."
              />
            ) : (
              <ul className="space-y-3">
                {upcoming.map((s) => (
                  <li key={s.id} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0 hover:bg-gray-50 -mx-2 px-2 rounded-md transition-colors" data-testid={`dashboard-upcoming-${s.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate">{s.label || AGENT_LABEL[s.agent_key] || s.agent_key}</div>
                        <div className="text-[11px] text-gray-500 font-mono mt-0.5">{cadenceText(s)}</div>
                      </div>
                      <div className={`shrink-0 text-[11px] font-mono font-bold px-2 py-1 rounded-md ${
                        new Date(s.next_run_at).getTime() <= nowMs
                          ? "text-orange-700 bg-orange-50 ring-1 ring-orange-200"
                          : "text-gray-700 bg-gray-100"
                      }`}>
                        {timeUntil(s.next_run_at)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Recent Agent Runs table */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Activity</div>
              <h3 className="text-xl font-black tracking-tight mt-0.5">Recent Agent Runs</h3>
            </div>
            <div className="text-[11px] font-mono text-gray-500">Last {recentRuns.length}</div>
          </div>
          <table className="w-full text-sm" data-testid="dashboard-recent-runs">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["When", "Agent", "Category", "Prompt", "Status", "By"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentRuns.map((r) => {
                const cat = AGENT_CATEGORY[r.agent_key] || "core";
                return (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`dashboard-run-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="text-[13px] font-bold text-gray-900">{AGENT_LABEL[r.agent_key] || r.agent_name}</div>
                      <div className="text-[10px] font-mono text-gray-500">{r.method} {r.oracle_record_id ? `· ${r.oracle_record_id}` : ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] tracking-[0.15em] uppercase font-bold ${cat === "finance_efficiency" ? "text-[#0020C2]" : "text-gray-600"}`}>
                        {cat.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-md truncate">{r.prompt}</td>
                    <td className="px-4 py-3"><StatusPill status={r.oracle_status} /></td>
                    <td className="px-4 py-3 font-mono text-[11px] text-gray-600">{r.created_by || "—"}</td>
                  </tr>
                );
              })}
              {recentRuns.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">
                  {loading ? "Loading…" : "No agent runs yet — pick an ERP agent from the sidebar to start."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}